<script lang="ts" setup>
import { computed, PropType } from "vue";
import { RouteLocationRaw } from "vue-router";

// type Colors = "primary" | "white" | "gray" | "link";
type Colors = "primary" | "link";
type ButtonTypes = "button" | "reset" | "submit";
type Sizes = "l" | "m" | "s";
type FontWeight = "regular" | "bold";
type Targets = "" | "_blank" | "_self" | "_parent" | "_top";
type Fonts = "regular" | "bold";

defineOptions({
  name: "UiTextButton",
});

const props = defineProps({
  color: {
    type: String as PropType<Colors>,
    default: "link",
  },

  label: {
    type: String,
    required: true,
  },

  type: {
    type: String as PropType<ButtonTypes>,
    default: "button",
  },

  size: {
    type: String as PropType<Sizes>,
    default: "l",
  },

  fontWeight: {
    type: String as PropType<FontWeight>,
    default: "bold",
  },

  href: {
    type: String,
    default: "",
  },

  to: {
    type: Object as PropType<RouteLocationRaw>,
    default: null,
  },

  target: {
    type: String as PropType<Targets>,
    default: "",
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  font: {
    type: String as PropType<Fonts>,
    default: "regular",
  },
});

const currentTag = computed(() => {
  if (props.href && !props.to) {
    return "a";
  } else if (!props.href && props.to) {
    return "router-link";
  } else {
    return "button";
  }
});
</script>

<template>
  <component
    :is="currentTag"
    :href="href"
    :disabled="disabled"
    :to="to"
    :target="target"
    class="ui-text-button"
    :class="[
      `ui-text-button__color_${color}`,
      `ui-text-button__size_${size}_${fontWeight}`,
    ]"
    :type="type"
  >
    <slot name="left" />

    <span class="ui-text-button__label label">{{ label }}</span>

    <slot name="right" />
  </component>
</template>

<style scoped lang="scss">
.ui-text-button {
  border: none;
  width: fit-content;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  gap: 4px;
  background: none;
  padding: 0;
  text-decoration: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  transition: color 0.15s ease-out;
  user-select: none;

  &:deep(.icon) {
    transition: background 0.15s ease-out;
  }

  @media (hover: none) {
    &:active {
      color: var(--color-semantic-content-hover-link);

      &:deep(.icon) {
        background: var(--color-semantic-content-hover-link);
      }
    }
  }

  &:disabled {
    cursor: not-allowed !important;
  }

  &__color {
    &_primary {
      color: var(--color-semantic-content-normal-primary);

      ::v-deep(.icon) {
        background: var(--color-semantic-content-normal-primary);
      }

      &:hover {
        color: var(--color-semantic-content-hover-primary);

        ::v-deep(.icon) {
          background: var(--color-semantic-content-hover-primary);
        }
      }
    }

    &_link {
      color: var(--color-semantic-content-normal-link);

      ::v-deep(.icon) {
        background: var(--color-semantic-content-normal-link);
      }

      &:hover {
        color: var(--color-semantic-content-hover-link);

        ::v-deep(.icon) {
          background: var(--color-semantic-content-hover-link);
        }
      }
    }
  }

  &__size {
    &_l {
      &_regular {
        @include body-M;
      }

      &_bold {
        @include body-bold-M;
        font-weight: 600;
      }

      &:deep(span.icon) {
        width: 24px;
        height: 24px;
      }
    }

    &_m {
      &_regular {
        @include body-S;
      }

      &_bold {
        @include body-bold-S;
      }

      &:deep(span.icon) {
        width: 20px;
        height: 20px;
      }
    }

    &_s {
      &_regular {
        @include caption;
      }

      &_bold {
        @include caption-bold-local;
      }

      &:deep(span.icon) {
        width: 16px;
        height: 16px;
      }

      @media screen and (max-width: 1023px) {
        .label {
          font-size: 10px;
        }
      }
    }
  }

  &_font {
    &_bold {
      font-weight: 600;
    }
  }
}
</style>
